import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "#app/utils/misc.tsx";

const buttonVariants = cva(
  "group/button inline-flex cursor-pointer shrink-0 items-center justify-center rounded-4xl border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:bg-transparent dark:hover:bg-input/30",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-zinc-200/80 dark:hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:text-white dark:hover:bg-destructive dark:hover:text-white dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
        // Local additions on top of the registry. The Supervity brand gradient,
        // replacing the pre-redesign `brand` / `brandOutline` variants.
        gradient: "bg-linear-to-r from-[#5b66aa] to-[#909cca] text-white",
        "gradient-outline":
          "border-[#D0D6ED] bg-white text-[#5b66aa] hover:border-[#909cca] hover:bg-linear-to-r hover:from-[#F0F4FF] hover:to-white dark:bg-zinc-900",
      },
      size: {
        default:
          "h-9 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        xs: "h-6 gap-1 px-2.5 text-xs has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        lg: "h-10 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
        // Local addition: wide, low-height CTA (the old `pill` size).
        pill: "h-auto gap-1.5 px-12 py-3 leading-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * Local addition. Base UI warns when its `nativeButton` assumption does not
 * match what `render` actually produces, and this codebase slots Remix `<Link>`
 * (an `<a>`) into buttons in ~90 places. We can be sure only for an intrinsic
 * element; for a component we infer from link-ish props and otherwise leave
 * Base UI's default, since a component may well render a real <button>.
 */
function inferNativeButton(render: unknown): boolean | undefined {
  if (!React.isValidElement(render)) return undefined;
  if (typeof render.type === "string") return render.type === "button";
  const props = render.props as { to?: unknown; href?: unknown };
  if (props?.to !== undefined || props?.href !== undefined) return false;
  return undefined;
}

function Button({
  className,
  variant = "default",
  size = "default",
  nativeButton,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  nativeButton ??= inferNativeButton(props.render);
  return (
    <ButtonPrimitive
      data-slot="button"
      nativeButton={nativeButton}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

/**
 * Local addition: the registry exports only the component, but `paginations`
 * and `status-button` build on the button's prop type.
 */
export type ButtonProps = React.ComponentProps<typeof Button>;

export { Button, buttonVariants };
